/**
 * THE FRONT DOOR — D-EZ-103.
 *
 * The game used to open on a race-week form with a "Seed" field. A first-time
 * visitor now sees one friendly card that says what the game IS and offers the
 * three ways in:
 *
 *   Start a new league          → league creation (the one season start)
 *   Just run one race           → an exhibition, track of your choosing
 *   Load a saved league         → the Account area (account save, this device, a file)
 *
 * `NextSeasonCard` is the same doorway for a league between seasons: the year is
 * settled, and the next one can be raced race by race or simulated whole.
 */

import { SEASON_RACES } from '../domain/schedule.ts';

export interface FrontDoorProps {
  onNewLeague: () => void;
  onExhibition: () => void;
  onLoad: () => void;
  /** The newest league kept on this device, if any: offered first as "Continue". */
  resume?: { label: string; onResume: () => void; error?: string | null } | null;
}

export default function FrontDoor({ onNewLeague, onExhibition, onLoad, resume }: FrontDoorProps) {
  return (
    <section className="door" aria-labelledby="door-h">
      <div className="door__flag" aria-hidden="true" />
      <p className="kicker">Red White &amp; Blue Racin&rsquo; · the stock car tabletop</p>
      <h1 id="door-h" className="door__h">
        You run a stock-car league.
      </h1>
      <p className="door__dek">Start your first season.</p>
      <p className="door__body">
        {SEASON_RACES} races a year on real dice and real charts, a playoff and a champion, then a winter of
        contracts, retirements and rookies. Every roll has a story, and every result goes in the book.
      </p>
      <div className="door__acts">
        {resume && (
          <>
            <button type="button" className="btn btn--hot btn--big door__btn" onClick={resume.onResume}>
              Continue — {resume.label}
            </button>
            {resume.error && <p className="err" role="alert">{resume.error}</p>}
          </>
        )}
        <button
          type="button"
          className={resume ? 'btn door__btn' : 'btn btn--hot btn--big door__btn'}
          onClick={onNewLeague}
        >
          Start a new league
        </button>
        <button type="button" className="btn door__btn" onClick={onExhibition}>
          Just run one race (exhibition)
        </button>
        <button type="button" className="btn btn--quiet door__btn" onClick={onLoad}>
          Load a saved league
        </button>
      </div>
    </section>
  );
}

export interface NextSeasonCardProps {
  leagueName: string;
  /** The season just settled. */
  seasonNumber: number;
  /** One line about how it ended, if known ("Zeke Ashford took the Cup."). */
  championLine?: string | null;
  onPlay: () => void;
  onSimulate: () => void;
  onReadLeague: () => void;
  onNewLeague: () => void;
  busy?: boolean;
}

export function NextSeasonCard({
  leagueName,
  seasonNumber,
  championLine,
  onPlay,
  onSimulate,
  onReadLeague,
  onNewLeague,
  busy = false,
}: NextSeasonCardProps) {
  const next = seasonNumber + 1;
  return (
    <section className="door door--next" aria-labelledby="next-h">
      <div className="door__flag" aria-hidden="true" />
      <p className="kicker">
        {leagueName} · season {seasonNumber} is in the books
      </p>
      <h1 id="next-h" className="door__h">
        Season {next} is on the calendar.
      </h1>
      {championLine && <p className="door__dek">{championLine}</p>}
      <p className="door__body">
        The winter is over: the same league, the same garage, a year older. Race it one weekend at a time, or let
        the whole year run and read about it after.
      </p>
      <div className="door__acts">
        <button type="button" className="btn btn--hot btn--big door__btn" onClick={onPlay} disabled={busy}>
          Start season {next} — race by race
        </button>
        <button type="button" className="btn door__btn" onClick={onSimulate} disabled={busy}>
          Simulate all of season {next}
        </button>
        <button type="button" className="btn btn--quiet door__btn" onClick={onReadLeague}>
          Read the league
        </button>
        <button type="button" className="btn btn--quiet door__btn" onClick={onNewLeague} disabled={busy}>
          Start a different league
        </button>
      </div>
    </section>
  );
}
